import { useState } from "react";
import { useTokensQuery, useCreateToken, useDeleteToken } from "../../../lib/api/tokens.api";
import { SectionLabel } from "./SettingsUIComponents";

export function TokensSection() {
  const { data: tokens } = useTokensQuery();
  const createToken = useCreateToken();
  const deleteToken = useDeleteToken();
  const [showCreate, setShowCreate] = useState(false);
  const [newTokenName, setNewTokenName] = useState("");
  const [newTokenDays, setNewTokenDays] = useState<number | null>(7);
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!newTokenName.trim()) return;
    setError(null);
    try {
      const result = await createToken.mutateAsync({
        name: newTokenName.trim(),
        expiresInDays: newTokenDays,
      });
      setCreatedToken(result.token);
      setShowCreate(false);
      setNewTokenName("");
      setNewTokenDays(7);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur lors de la création");
    }
  };

  const handleCopy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDelete = async (id: string) => {
    await deleteToken.mutateAsync(id);
    setDeleteConfirm(null);
  };

  const formatDate = (d: string) => new Date(d).toLocaleDateString("fr-FR");

  return (
    <div className="flex flex-col gap-3">
      <SectionLabel>Palnia CLI</SectionLabel>
      <p className="text-[11px] text-ink-muted">
        Installez le CLI Palnia pour gérer vos tâches, événements et habitudes depuis le terminal.
      </p>

      {/* Instructions d'installation */}
      <div className="flex flex-col gap-2 p-3 bg-surface-alt rounded-xl border border-border">
        <p className="text-[11px] font-semibold text-ink">Installation rapide</p>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2 group">
            <span className="text-[10px] text-ink-muted font-mono bg-surface px-1.5 py-0.5 rounded text-ink-faint">npm</span>
            <code className="flex-1 text-[11px] font-mono bg-surface px-2 py-1 rounded border border-border/60 text-ink select-all">
              npm install -g @oalacea/palnia-cli
            </code>
          </div>

          <div className="flex items-center gap-2 group">
            <span className="text-[10px] text-ink-muted font-mono bg-surface px-1.5 py-0.5 rounded text-ink-faint">cargo</span>
            <code className="flex-1 text-[11px] font-mono bg-surface px-2 py-1 rounded border border-border/60 text-ink select-all">
              cargo install palnia-cli
            </code>
          </div>
        </div>

        <p className="text-[10px] text-ink-muted">
          Après installation : <code className="font-mono bg-surface px-1 rounded">palnia login</code>
        </p>
      </div>

      <div className="h-px bg-border/40 my-1" />

      <p className="text-[11px] text-ink-muted">
        Créez des tokens pour authentifier le CLI.
      </p>

      {/* Banner token créé */}
      {createdToken && (
        <div className="flex flex-col gap-2 p-3 bg-accent/5 rounded-xl border border-accent/20">
          <p className="text-[11px] font-semibold text-accent">
            Token créé — copiez-le maintenant, il ne sera plus affiché.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-[11px] font-mono bg-surface px-2.5 py-1.5 rounded-md break-all select-all text-ink border border-border/60">
              {createdToken}
            </code>
            <button
              type="button"
              onClick={() => handleCopy(createdToken)}
              className="shrink-0 px-3 py-2 text-xs font-medium bg-accent text-white rounded-md hover:opacity-90 transition-colors"
            >
              {copied ? "Copié !" : "Copier"}
            </button>
          </div>
          <button
            type="button"
            onClick={() => setCreatedToken(null)}
            className="self-start text-[10px] text-ink-muted hover:text-ink transition-colors"
          >
            Fermer
          </button>
        </div>
      )}

      {/* Liste des tokens */}
      {tokens && tokens.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {tokens.map((t) => (
            <div key={t.id} className="flex items-center justify-between gap-3 py-2 px-1">
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="text-xs font-medium text-ink truncate">{t.name}</span>
                <span className="text-[10px] text-ink-muted font-mono">{t.prefix}...</span>
                <span className="text-[10px] text-ink-faint">
                  Créé {formatDate(t.createdAt)}
                  {t.expiresAt ? ` · Expire ${formatDate(t.expiresAt)}` : " · N'expire jamais"}
                  {t.lastUsedAt && ` · Utilisé ${formatDate(t.lastUsedAt)}`}
                </span>
              </div>
              {deleteConfirm === t.id ? (
                <div className="flex gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => handleDelete(t.id)}
                    className="px-2 py-1 text-[10px] font-medium text-danger hover:text-danger/80 rounded"
                  >
                    Confirmer
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteConfirm(null)}
                    className="px-2 py-1 text-[10px] font-medium text-ink-muted hover:text-ink rounded"
                  >
                    Annuler
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setDeleteConfirm(t.id)}
                  className="shrink-0 px-2 py-1 text-[10px] font-medium text-danger/70 hover:text-danger rounded"
                >
                  Révoquer
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {tokens && tokens.length === 0 && !createdToken && (
        <p className="text-[11px] text-ink-faint italic">Aucun token actif.</p>
      )}

      {error && (
        <p className="text-[11px] text-danger px-1">{error}</p>
      )}

      {/* Formulaire de création */}
      {showCreate ? (
        <div className="flex flex-col gap-3 p-3 bg-surface-alt rounded-xl border border-border">
          <input
            type="text"
            placeholder="Nom du token (ex: Mon CLI)"
            value={newTokenName}
            onChange={(e) => setNewTokenName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); }}
            maxLength={50}
            autoFocus
            className="w-full px-3 py-2 text-xs text-ink bg-surface border border-border/60 rounded-md focus:outline-none focus:border-accent/60 placeholder:text-ink-faint"
          />
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-ink-muted shrink-0">Expiration</span>
            <div className="inline-flex rounded-md bg-surface p-0.5 border border-border/60">
              {([
                { value: 1, label: "1j" },
                { value: 7, label: "7j" },
                { value: 14, label: "14j" },
                { value: 30, label: "30j" },
                { value: null, label: "Jamais" },
              ] as const).map((opt) => (
                <button
                  key={String(opt.value)}
                  type="button"
                  onClick={() => setNewTokenDays(opt.value)}
                  className={`px-2.5 py-1 text-[11px] font-medium rounded transition-all ${
                    newTokenDays === opt.value
                      ? "bg-accent text-white shadow-sm"
                      : "text-ink-muted hover:text-ink"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => { setShowCreate(false); setNewTokenName(""); }}
              className="px-3 py-2 text-xs font-medium text-ink-muted hover:text-ink rounded-md"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleCreate}
              disabled={!newTokenName.trim() || createToken.isPending}
              className="px-3 py-2 text-xs font-medium bg-accent text-white rounded-md hover:opacity-90 disabled:opacity-40 transition-colors"
            >
              {createToken.isPending ? "Création..." : "Créer"}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          disabled={(tokens?.length ?? 0) >= 5}
          className="flex items-center gap-2 py-2 px-1 text-xs font-medium text-accent hover:text-accent/80 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent/10 text-accent text-sm">+</span>
          Générer un token
        </button>
      )}

      {(tokens?.length ?? 0) >= 5 && (
        <p className="text-[10px] text-ink-faint">Maximum 5 tokens atteint.</p>
      )}
    </div>
  );
}
